import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useUpdateApplicationStatus } from '../../../../hooks/mutations/useApplicationMutations';

interface HireOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateName: string;
  applicationId: string;
}

export default function HireOfferModal({ isOpen, onClose, candidateName, applicationId }: HireOfferModalProps) {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [onboardingNote, setOnboardingNote] = useState('Please bring 2 forms of ID and your direct deposit info.');

  // TODO: After hire, call Hourly oRPC `employees.create` to onboard the candidate
  // as an employee in the Hourly system. Requires: candidateName, startDate, companyId.
  // The oRPC procedure may not exist yet — check hourly backend before implementing.
  const updateStatus = useUpdateApplicationStatus();

  const handleSendOffer = () => {
    if (!startDate || !startTime) {
      alert('Please select both start date and time');
      return;
    }

    updateStatus.mutate(
      { applicationId, payload: { status: 'offer' } },
      {
        onSuccess: () => {
          setStep('success');
        },
      }
    );
  };

  const handleDone = () => {
    // Reset state
    setStep('form');
    setStartDate('');
    setStartTime('');
    setOnboardingNote('Please bring 2 forms of ID and your direct deposit info.');
    onClose();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={step === 'form' ? onClose : handleDone}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl animate-slide-up">
        {step === 'form' ? (
          <div className="px-6 pb-6 pt-4">
            {/* Handle Bar */}
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200 mb-6" />

            {/* Header */}
            <h2 className="text-2xl font-extrabold text-gray-900 mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Hiring {candidateName}
            </h2>

            {/* Form */}
            <div className="space-y-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full h-12 px-4 border border-gray-300 rounded-xl text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Start Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full h-12 px-4 border border-gray-300 rounded-xl text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Onboarding Note */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Onboarding Instructions
                </label>
                <textarea
                  value={onboardingNote}
                  onChange={(e) => setOnboardingNote(e.target.value)}
                  rows={4}
                  className="w-full p-4 border border-gray-300 rounded-xl text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Action Button */}
            <button
              type="button"
              onClick={handleSendOffer}
              disabled={updateStatus.isPending}
              className="w-full h-14 rounded-xl bg-green-600 text-white text-lg font-bold hover:bg-green-700 transition-colors mt-6 disabled:opacity-50"
            >
              {updateStatus.isPending ? 'Sending…' : 'Send Offer'}
            </button>
          </div>
        ) : (
          <div className="px-6 pb-6 pt-4">
            {/* Handle Bar */}
            <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200 mb-6" />

            {/* Success Content */}
            <div className="text-center py-8">
              <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-6" strokeWidth={2} />

              <h2 className="text-2xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Offer Sent!
              </h2>

              <p className="text-base text-gray-600 leading-relaxed mb-2">
                <span className="font-semibold">{candidateName}</span> has been notified via SMS.
              </p>

              <p className="text-sm text-gray-500 mb-8">
                They are scheduled to start on <span className="font-semibold">{formatDate(startDate)}</span> at{' '}
                <span className="font-semibold">{formatTime(startTime)}</span>.
              </p>

              <button
                type="button"
                onClick={handleDone}
                className="w-full h-14 rounded-xl bg-[#0156FF] text-white text-lg font-bold hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
